import { useLegacyStore } from '@warp-drive/legacy';
import { JSONAPICache } from '@warp-drive/json-api';
import { DefaultCachePolicy } from '@warp-drive/core/store';
import {
  createTioRequestHandlers,
  createTioRequestManager,
  type TioRequestManagerOptions,
} from './request-manager.ts';

type LegacyStoreOptions = Parameters<typeof useLegacyStore>[0];
type LegacySchemas = LegacyStoreOptions extends { schemas?: infer S } ? S : never;

/**
 * Store factory for WarpDrive-converted apps (ADR-001). Safe to call at
 * module scope: the handler chain is owner-free and reads request-time auth
 * state from `tio-data/auth-context` (installed by the app shell at boot).
 *
 * `legacyRequests: true` keeps `LegacyNetworkHandler` + Model `.save()`
 * semantics during the transition; `schemas` start empty and grow as apps
 * opt resources out of the legacy Model path (Phase 1.3+). Fetch and
 * CacheHandler are appended by `useLegacyStore` itself.
 */
export interface TioSchemaManifest {
  schemas?: LegacySchemas;
  /** LegacyTrait objects (data/traits/*.schema.ts default exports). */
  traits?: unknown[];
  /** Extension registrations (data/resources/*.ext.ts default exports). */
  extensions?: unknown[];
}

export default function createStore(
  options: TioRequestManagerOptions,
  manifest: TioSchemaManifest = {}
) {
  const { schemas = [], traits = [], extensions = [] } = manifest;

  // Toggle the @warp-drive/core framework-probe patch (see TioRequestManagerOptions
  // .strictExtensionProbes + patches/@warp-drive__core@5.8.2.patch). Runs at store-module
  // eval (boot), before any record is materialized, so the patch reads the right value.
  // Default lenient (false): isQueryParams/unknownProperty/toHTML return undefined in dev
  // instead of throwing. No production effect (the assertion is DEBUG-gated).
  (globalThis as Record<string, unknown>).WARP_DRIVE_STRICT_EXTENSION_PROBES =
    options.strictExtensionProbes ?? false;

  const LegacyStore = useLegacyStore({
    legacyRequests: false,
    cache: JSONAPICache,
    // Explicit request cache-freshness policy. `useLegacyStore` otherwise defaults
    // this to `new DefaultCachePolicy({ apiCacheSoftExpires: 30s, apiCacheHardExpires: 15m })`
    // — we make it explicit (visible/tunable) and raise the soft window. Semantics:
    // under soft → served from cache, no refetch; between soft and hard → served
    // immediately + revalidated in the background (stale-while-revalidate); past hard
    // → blocking refetch. Global default here is the "semi-stable" tier (soft 5m /
    // hard 30m): TIO data is mostly stable, so 5m soft cuts the framework default's
    // every-30s background revalidation without making reads meaningfully stale.
    // Per-resource tuning (reference data longer; volatile/workflow data shorter)
    // belongs on individual request builders via `cacheOptions` (follow-up; see
    // warpdrive-migration-loop tiers R/S/V/L). `constraints.headers` is a no-op today
    // (tio-api emits no Cache-Control/Expires) but kept so server headers can drive
    // invalidation if/when the API adds them.
    policy: new DefaultCachePolicy({
      apiCacheSoftExpires: 5 * 60 * 1000, // 5 minutes
      apiCacheHardExpires: 30 * 60 * 1000, // 30 minutes
      constraints: {
        headers: {
          'X-WarpDrive-Expires': true,
          'Cache-Control': true,
          Expires: true,
        },
      },
    }),
    schemas,
    // Undocumented-but-supported by the 5.8.2 runtime (the type declares
    // only `schemas`; the impl also reads traits/derivations/transformations/
    // CAUTION_MEGA_DANGER_ZONE_extensions — see DIVERGENCE_LOG #16).
    // Finalization is lazy (first use), so registration order is safe.
    ...({
      traits,
      CAUTION_MEGA_DANGER_ZONE_extensions: extensions,
    } as Record<string, unknown>),
    handlers: createTioRequestHandlers(options),
    linksMode: false,
  });

  // DIVERGENCE_LOG #19: `useLegacyStore` hardcodes its RequestManager chain as
  // `[LegacyNetworkHandler, ...options.handlers, Fetch]` — it ALWAYS prepends
  // its own LegacyNetworkHandler at index 0, ahead of anything in
  // `options.handlers`, and accepts no `requestManager` override. For apps with
  // NO demo handler (tio-agent) that leading LegacyNetworkHandler is
  // behaviorally identical to ours, so the handlers-option path is fine. But
  // the TioDemoHandler MUST run BEFORE LegacyNetworkHandler (it intercepts the
  // legacy findRecord/query ops LegacyNetworkHandler would otherwise route to
  // the adapter), and no `options.handlers` entry can precede index 0. So we
  // override the `requestManager` field in a subclass with our own
  // correctly-ordered chain (`createTioRequestManager` puts demo first). The
  // subclass field initializer runs after the parent's, so it wins; the
  // throwaway manager useLegacyStore built is discarded. Harmless for
  // demo-less apps (same handler order); load-bearing for employee/admin.
  class TioLegacyStore extends LegacyStore {
    requestManager = createTioRequestManager(options);
  }
  return TioLegacyStore;
}
